import React, { useEffect, useMemo, useState } from "react";
import { auth, db } from "../lib/firebase";
import {
  doc,
  setDoc,
  getDoc,
  onSnapshot,
} from "firebase/firestore";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";

type CimData = {
  companyName?: string;
  ebitda?: number;
  adjustments?: string; // raw text, one per line: "Description: Value"
  projectedFCF1?: number;
  projectedFCF2?: number;
  projectedFCF3?: number;
  growthRate?: number; // 0.025 = 2.5%
  wacc?: number;       // 0.085 = 8.5%
  financialSummary?: string;
  kpis?: string;       // multi-line
  totalValuation?: number;
  timestamp?: Date;
};

type AdjItem = { description: string; value: number; checked: boolean };

function formatMoney(n: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
}

// Get Firebase app ID from config
const getAppId = () => {
  const firebaseConfig = auth.app.options;
  return firebaseConfig.projectId || "default-app-id";
};

export default function CIMReview() {
  const { user, loading: loadingUser } = useAuth();
  const userId = user?.id ?? "";

  // Form state
  const [companyName, setCompanyName] = useState("");
  const [ebitda, setEbitda] = useState<number | "">("");
  const [adjustmentsText, setAdjustmentsText] = useState("");
  const [fcf1, setFcf1] = useState<number | "">("");
  const [fcf2, setFcf2] = useState<number | "">("");
  const [fcf3, setFcf3] = useState<number | "">("");
  const [growthRatePct, setGrowthRatePct] = useState<number | "">(""); // in %
  const [waccPct, setWaccPct] = useState<number | "">("");             // in %
  const [financialSummary, setFinancialSummary] = useState("");
  const [kpis, setKpis] = useState("");

  const [submissionId, setSubmissionId] = useState("");
  const [message, setMessage] = useState<{ type: "success" | "error" | "info"; text: string } | null>(null);

  // Interactive list of adjustments (generated from text)
  const adjustments: AdjItem[] = useMemo(() => {
    return adjustmentsText
      .split("\n")
      .map((l) => l.trim())
      .filter((l) => l.length)
      .map((line) => {
        const [descPart, valuePart] = line.split(":");
        const description = (descPart ?? "").trim() || "Adjustment";
        const value = parseFloat((valuePart ?? "").trim().replace(",", ".")) || 0;
        return { description, value, checked: true };
      });
  }, [adjustmentsText]);

  const [checkedMap, setCheckedMap] = useState<Record<number, boolean>>({});

  useEffect(() => {
    if (!userId) return;
    const appId = getAppId();
    const ref = doc(db, `artifacts/${appId}/users/${userId}/valuations/cim-data`);
    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        const data = snap.data() as CimData;
        setCompanyName(data.companyName ?? "");
        setEbitda(Number.isFinite(data.ebitda!) ? data.ebitda! : "");
        setAdjustmentsText(data.adjustments ?? "");
        setFcf1(Number.isFinite(data.projectedFCF1!) ? data.projectedFCF1! : "");
        setFcf2(Number.isFinite(data.projectedFCF2!) ? data.projectedFCF2! : "");
        setFcf3(Number.isFinite(data.projectedFCF3!) ? data.projectedFCF3! : "");
        setGrowthRatePct(
          Number.isFinite(data.growthRate!) ? Math.round((data.growthRate! * 100 + Number.EPSILON) * 100) / 100 : ""
        );
        setWaccPct(
          Number.isFinite(data.wacc!) ? Math.round((data.wacc! * 100 + Number.EPSILON) * 100) / 100 : ""
        );
        setFinancialSummary(data.financialSummary ?? "");
        setKpis(data.kpis ?? "");
      }
    });
    return () => unsub();
  }, [userId]);

  // recalc adjusted EBITDA
  const adjustedEbitda = useMemo(() => {
    const base = typeof ebitda === "number" ? ebitda : 0;
    let totalAdj = 0;
    adjustments.forEach((a, idx) => {
      const isChecked =
        typeof checkedMap[idx] === "boolean" ? checkedMap[idx] : a.checked;
      if (isChecked) totalAdj += a.value;
    });
    return base + totalAdj;
  }, [ebitda, adjustments, checkedMap]);

  const handleToggleAdj = (idx: number) => {
    setCheckedMap((m) => ({ ...m, [idx]: !(m[idx] ?? true) }));
  };

  // DCF calculation
  const valuation = useMemo(() => {
    const _fcf1 = typeof fcf1 === "number" ? fcf1 : 0;
    const _fcf2 = typeof fcf2 === "number" ? fcf2 : 0;
    const _fcf3 = typeof fcf3 === "number" ? fcf3 : 0;
    const _g = typeof growthRatePct === "number" ? growthRatePct / 100 : 0;
    const _w = typeof waccPct === "number" ? waccPct / 100 : 0;

    if (_w <= 0 || _g >= _w) return { ok: false as const, total: 0, pv1: 0, pv2: 0, pv3: 0, pvTerminal: 0 };

    const pv1 = _fcf1 / (1 + _w);
    const pv2 = _fcf2 / Math.pow(1 + _w, 2);
    const pv3 = _fcf3 / Math.pow(1 + _w, 3);
    const terminal = (_fcf3 * (1 + _g)) / (_w - _g);
    const pvTerminal = terminal / Math.pow(1 + _w, 3);
    const total = pv1 + pv2 + pv3 + pvTerminal;
    return { ok: true as const, total, pv1, pv2, pv3, pvTerminal };
  }, [fcf1, fcf2, fcf3, growthRatePct, waccPct]);

  const showMsg = (type: "success" | "error" | "info", text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 4000);
  };

  const onGenerateAndSave = async () => {
    setMessage(null);

    const _w = typeof waccPct === "number" ? waccPct / 100 : 0;
    const _g = typeof growthRatePct === "number" ? growthRatePct / 100 : 0;
    if (!companyName || _w === 0 || _g >= _w) {
      showMsg(
        "error",
        "Check inputs: WACC must be > 0 and growth rate < WACC."
      );
      return;
    }

    try {
      const appId = getAppId();
      const ref = doc(db, `artifacts/${appId}/users/${userId}/valuations/cim-data`);
      await setDoc(ref, {
        companyName,
        ebitda: typeof ebitda === "number" ? ebitda : 0,
        adjustments: adjustmentsText,
        projectedFCF1: typeof fcf1 === "number" ? fcf1 : 0,
        projectedFCF2: typeof fcf2 === "number" ? fcf2 : 0,
        projectedFCF3: typeof fcf3 === "number" ? fcf3 : 0,
        growthRate: typeof growthRatePct === "number" ? growthRatePct / 100 : 0,
        wacc: typeof waccPct === "number" ? waccPct / 100 : 0,
        financialSummary,
        kpis,
        totalValuation: valuation.ok ? valuation.total : 0,
        timestamp: new Date(),
      } as CimData);
      showMsg("success", "Data saved successfully!");
    } catch (e) {
      console.error(e);
      showMsg("error", "Save error. Check console.");
    }
  };

  const onLoadSubmission = async () => {
    setMessage(null);
    const id = submissionId.trim();
    if (!id) {
      showMsg("info", "Enter a submission ID.");
      return;
    }
    try {
      const appId = getAppId();
      const ref = doc(db, `artifacts/${appId}/users/${id}/valuations/cim-data`);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data() as CimData;
        setCompanyName(data.companyName ?? "");
        setEbitda(Number.isFinite(data.ebitda!) ? data.ebitda! : "");
        setAdjustmentsText(data.adjustments ?? "");
        setFcf1(Number.isFinite(data.projectedFCF1!) ? data.projectedFCF1! : "");
        setFcf2(Number.isFinite(data.projectedFCF2!) ? data.projectedFCF2! : "");
        setFcf3(Number.isFinite(data.projectedFCF3!) ? data.projectedFCF3! : "");
        setGrowthRatePct(
          Number.isFinite(data.growthRate!) ? Math.round((data.growthRate! * 100 + Number.EPSILON) * 100) / 100 : ""
        );
        setWaccPct(
          Number.isFinite(data.wacc!) ? Math.round((data.wacc! * 100 + Number.EPSILON) * 100) / 100 : ""
        );
        setFinancialSummary(data.financialSummary ?? "");
        setKpis(data.kpis ?? "");
        showMsg("success", "Data loaded for review!");
      } else {
        showMsg("error", "No submission found for this ID.");
      }
    } catch (e) {
      console.error(e);
      showMsg("error", "Load error. Check console.");
    }
  };

  if (loadingUser) {
    return (
      <div className="p-8 text-muted-foreground">Loading session…</div>
    );
  }

  if (!user) {
    return <Navigate to="/login?next=/cim-review" replace />;
  }

  return (
    <div className="max-w-7xl mx-auto py-8 px-4 grid md:grid-cols-2 gap-8 bg-background">
      {/* FORM */}
      <div className="space-y-6">
        <div className="bg-card rounded-xl shadow p-6 border">
          <h1 className="text-2xl font-bold mb-4 text-foreground">CIM Generator – Review</h1>
          <p className="text-xs text-muted-foreground mb-4">User ID: {userId}</p>

          {/* Load a submission */}
          <div className="mb-6 pb-4 border-b">
            <h2 className="text-lg font-semibold mb-2 text-foreground">Load a Submission</h2>
            <p className="text-sm text-muted-foreground mb-3">
              Enter the user ID of the submission to pre-fill the form.
            </p>
            <div className="flex gap-2">
              <input
                className="flex-1 border rounded-md px-3 py-2 bg-background text-foreground"
                placeholder="Submission ID"
                value={submissionId}
                onChange={(e) => setSubmissionId(e.target.value)}
              />
              <button
                onClick={onLoadSubmission}
                className="px-3 py-2 rounded-md bg-accent text-accent-foreground hover:bg-accent/90"
              >
                Load
              </button>
            </div>
          </div>

          {/* Company info */}
          <div className="space-y-3">
            <label className="block">
              <span className="text-sm font-medium text-foreground">Company Name</span>
              <input
                className="mt-1 w-full border rounded-md px-3 py-2 bg-background text-foreground"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Ex: Tech Innovations Inc."
              />
            </label>

            <div className="pt-4 mt-2 border-t">
              <h2 className="text-lg font-semibold mb-1 text-foreground">EBITDA Analysis</h2>
              <p className="text-sm text-muted-foreground mb-3">
                Enter EBITDA and adjustments (positive or negative).
                Format per line: <code>Description: Value</code>
              </p>

              <div className="grid sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-sm font-medium text-foreground">Gross EBITDA (M$)</span>
                  <input
                    type="number"
                    className="mt-1 w-full border rounded-md px-3 py-2 bg-background text-foreground"
                    value={ebitda}
                    onChange={(e) => setEbitda(e.target.value === "" ? "" : parseFloat(e.target.value))}
                    placeholder="Ex: 15"
                  />
                </label>
                <div className="block">
                  <span className="text-sm font-medium text-foreground">Adjusted EBITDA</span>
                  <div className="mt-1 px-3 py-2 border rounded-md bg-secondary text-foreground font-semibold">
                    {formatMoney(adjustedEbitda || 0)}
                  </div>
                </div>
              </div>

              <label className="block mt-3">
                <span className="text-sm font-medium text-foreground">Adjustments (one per line)</span>
                <textarea
                  rows={6}
                  className="mt-1 w-full border rounded-md px-3 py-2 bg-background text-foreground"
                  value={adjustmentsText}
                  onChange={(e) => setAdjustmentsText(e.target.value)}
                  placeholder={`Owner salary: 150000\nExceptional legal fees: 50000\nGain on asset sale: -100000`}
                />
              </label>
            </div>

            <div className="pt-4 mt-2 border-t">
              <h2 className="text-lg font-semibold mb-1 text-foreground">Valuation Model (DCF)</h2>
              <p className="text-sm text-muted-foreground mb-3">
                Enter FCF, growth rate, and WACC.
              </p>

              <div className="grid sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-sm font-medium text-foreground">FCF Year 1 (M$)</span>
                  <input
                    type="number"
                    className="mt-1 w-full border rounded-md px-3 py-2 bg-background text-foreground"
                    value={fcf1}
                    onChange={(e) => setFcf1(e.target.value === "" ? "" : parseFloat(e.target.value))}
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-foreground">FCF Year 2 (M$)</span>
                  <input
                    type="number"
                    className="mt-1 w-full border rounded-md px-3 py-2 bg-background text-foreground"
                    value={fcf2}
                    onChange={(e) => setFcf2(e.target.value === "" ? "" : parseFloat(e.target.value))}
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-foreground">FCF Year 3 (M$)</span>
                  <input
                    type="number"
                    className="mt-1 w-full border rounded-md px-3 py-2 bg-background text-foreground"
                    value={fcf3}
                    onChange={(e) => setFcf3(e.target.value === "" ? "" : parseFloat(e.target.value))}
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-foreground">Terminal Growth Rate (%)</span>
                  <input
                    type="number"
                    className="mt-1 w-full border rounded-md px-3 py-2 bg-background text-foreground"
                    value={growthRatePct}
                    onChange={(e) => setGrowthRatePct(e.target.value === "" ? "" : parseFloat(e.target.value))}
                    placeholder="Ex: 2.5"
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-foreground">WACC (%)</span>
                  <input
                    type="number"
                    className="mt-1 w-full border rounded-md px-3 py-2 bg-background text-foreground"
                    value={waccPct}
                    onChange={(e) => setWaccPct(e.target.value === "" ? "" : parseFloat(e.target.value))}
                    placeholder="Ex: 8.5"
                  />
                </label>
              </div>
            </div>

            <div className="pt-4 mt-2 border-t">
              <h2 className="text-lg font-semibold mb-1 text-foreground">General Information</h2>
              <label className="block">
                <span className="text-sm font-medium text-foreground">Financial Overview</span>
                <textarea
                  rows={4}
                  className="mt-1 w-full border rounded-md px-3 py-2 bg-background text-foreground"
                  value={financialSummary}
                  onChange={(e) => setFinancialSummary(e.target.value)}
                  placeholder="Describe financial health, assets, liabilities…"
                />
              </label>
              <label className="block mt-3">
                <span className="text-sm font-medium text-foreground">KPIs</span>
                <textarea
                  rows={6}
                  className="mt-1 w-full border rounded-md px-3 py-2 bg-background text-foreground"
                  value={kpis}
                  onChange={(e) => setKpis(e.target.value)}
                  placeholder={"Retention rate\nNumber of customers\n…" }
                />
              </label>
            </div>

            {message && (
              <div
                className={`mt-2 rounded-md px-3 py-2 text-sm ${
                  message.type === "success"
                    ? "bg-green-100 text-green-800 border border-green-200"
                    : message.type === "error"
                    ? "bg-red-100 text-red-800 border border-red-200"
                    : "bg-yellow-100 text-yellow-800 border border-yellow-200"
                }`}
              >
                {message.text}
              </div>
            )}

            <button
              onClick={onGenerateAndSave}
              className="w-full mt-2 px-4 py-3 rounded-md bg-accent text-accent-foreground font-semibold hover:bg-accent/90"
            >
              Generate and Save CIM
            </button>
          </div>
        </div>
      </div>

      {/* PREVIEWS */}
      <div className="space-y-6">
        {/* Interactive adjustments */}
        <div className="bg-card rounded-xl shadow p-6 border">
          <h2 className="text-xl font-bold mb-3 text-foreground">Interactive Adjustments</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Check/uncheck to see the impact on adjusted EBITDA.
          </p>
          <div className="space-y-3">
            {adjustments.length === 0 ? (
              <p className="text-sm text-muted-foreground">No adjustments entered.</p>
            ) : (
              adjustments.map((a, idx) => {
                const isChecked =
                  typeof checkedMap[idx] === "boolean" ? checkedMap[idx] : a.checked;
                return (
                  <label key={idx} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => handleToggleAdj(idx)}
                    />
                    <span className="text-sm text-foreground">{a.description}</span>
                    <span className="text-sm font-semibold text-foreground">
                      {formatMoney(a.value)}
                    </span>
                  </label>
                );
              })
            )}
          </div>
          <div className="mt-4 pt-3 border-t">
            <p className="text-base font-bold text-foreground">
              Adjusted EBITDA:{" "}
              <span className="font-extrabold text-accent">
                {formatMoney(adjustedEbitda || 0)}
              </span>
            </p>
          </div>
        </div>

        {/* CIM Preview (summary) */}
        <div className="bg-card rounded-xl shadow p-6 border">
          <h2 className="text-xl font-bold mb-4 text-foreground">CIM Preview</h2>

          <div className="space-y-3 text-foreground">
            <p>
              <strong>Company:</strong> {companyName || "—"}
            </p>
            <p>
              <strong>Gross EBITDA:</strong>{" "}
              {formatMoney((typeof ebitda === "number" ? ebitda : 0) || 0)}
            </p>
            <p>
              <strong>Adjusted EBITDA:</strong> {formatMoney(adjustedEbitda || 0)}
            </p>
          </div>

          <div className="mt-4 space-y-2 text-foreground">
            <p className="font-semibold">Discounted Cash Flows</p>
            <ul className="list-disc list-inside text-sm">
              <li>Year 1: {formatMoney(valuation.pv1 || 0)}</li>
              <li>Year 2: {formatMoney(valuation.pv2 || 0)}</li>
              <li>Year 3: {formatMoney(valuation.pv3 || 0)}</li>
            </ul>
            <p className="text-sm">
              <strong>Terminal Value (Discounted):</strong>{" "}
              {formatMoney(valuation.pvTerminal || 0)}
            </p>
            <p className="text-sm">
              <strong>Total:</strong>{" "}
              {valuation.ok ? formatMoney(valuation.total) : "—"}
            </p>
          </div>

          <div className="mt-6 space-y-2 text-foreground">
            <p className="font-semibold">Financial Overview</p>
            <p className="text-sm whitespace-pre-wrap">{financialSummary || "—"}</p>
            <p className="font-semibold mt-4">KPIs</p>
            {kpis ? (
              <ul className="list-disc list-inside text-sm whitespace-pre-wrap">
                {kpis.split("\n").map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            ) : (
              <p className="text-sm">—</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
